/**
 * Scripted bot players.
 *
 * A bot is a function from world state to commands — exactly the same commands
 * the UI emits. That means a bot run is a legal run, and anything the harness
 * discovers is something a human could have hit.
 *
 * Four personalities, chosen so their failure modes are different:
 *   turtle   — walls and towers first, minimal army, dies to sappers and fliers
 *   rush     — army first, no walls, dies when it cannot be everywhere
 *   balanced — the reasonable line a competent player would take
 *   greedy   — economy far too far out, dies to raiders and to its own sprawl
 *
 * If all four die at the same wave, the balance is flat. If one of them is
 * thirty waves better than the others, that strategy is the game.
 */

import { Cmd, cmd, type Command } from './commands.ts';
import { MAP_H, MAP_W, POP_PER_RUNG, VILLAGER_SHARE } from './constants.ts';
import { alliedWith, areHostile, Phase, RESOURCE_KEYS, Team, Terrain, UF, WallKind } from './types.ts';
import { Formation, Stance, type World } from './world.ts';
import { academyTier, buildCostFor, buildRefusal, canPlaceBuilding, hasTrainer, researchError, trainerCategoryFor, upgradeCostFor } from './apply.ts';
import { affordableModels, minMuster, musterStrength, reinforceError } from './systems/companies.ts';
import { larderWorth } from './systems/settlement.ts';
import { BUILDING_TYPES, buildingTypeById, buildingTypeIndex } from '../data/buildings.ts';
import { techFor, type TechNode } from '../data/tech.ts';
import { race } from '../data/races.ts';
import { ageCost, ageHearthTier, ageWaveFloor, MAX_AGE } from '../data/ages.ts';
import { unitType, unitTypeIndex } from '../data/units.ts';
import { canAfford } from './types.ts';
import { powerOrNull } from '../data/powers.ts';
import { resolvePower } from './systems/powers.ts';
import { relicsAvailable } from '../data/relics.ts';
import { itemDef } from '../data/items.ts';
import { equippedStack, hasForge, INVENTORY_MAX, reforgeError } from './systems/items.ts';
import { chronicleEvent } from '../data/events.ts';
import { nextInt, nextFloat, type Rng, makeRng, deriveSeed } from '../core/rng.ts';

export type BotKind = 'turtle' | 'rush' | 'balanced' | 'greedy';

export const BOT_KINDS: BotKind[] = ['turtle', 'rush', 'balanced', 'greedy'];

/**
 * Bots buy *roles*, not unit ids.
 *
 * The first version listed Ashen unit ids in preference order, which produced
 * two problems the harness could not see past: every bot fielded exactly the two
 * cheapest units on its list forever, and a non-Ashen run fell back to raw
 * roster order so per-faction numbers meant nothing. Roles fix both — each bot
 * has a target composition, and it buys whatever its army is currently shortest
 * of, from whatever its own faction can actually drill.
 */
export type Role = 'line' | 'shot' | 'horse' | 'heavy' | 'caster';

const ROLES: Role[] = ['line', 'shot', 'horse', 'heavy', 'caster'];

/**
 * What counts as a company rather than a cadre, as a share of the establishment.
 *
 * `MIN_MUSTER_SHARE` (a quarter, in `companies.ts`) is the floor the simulation
 * will raise a banner at; this is the share above which a bot considers the
 * muster *good*, and the two are deliberately different numbers. See
 * `pickInRole` for why the gap matters: without it the preference for the best
 * tier degenerates into a preference for the most expensive thing the town can
 * scrape a quarter of.
 */
const MUSTER_SOLID_SHARE = 0.6;

interface BotPlan {
  /** Build orders in priority order, as building ids. */
  buildOrder: string[];
  /** Target number of wall rings, and their radius. */
  wallRadius: number;
  wallWaves: number;
  /** How many companies it wants by wave n. */
  armyTarget: (wave: number) => number;
  /** Desired share of the army by role. Need not sum to exactly 1. */
  roleMix: Record<Role, number>;
  /** How far out it will place economy buildings. */
  econRadius: number;
  /** Willingness to spend Will. */
  castiness: number;
  towerTarget: (wave: number) => number;
  /** Fraction of stockpiled stone it is willing to sink into walls. */
  wallAppetite: number;
  /**
   * Waves past the floor before it will commit to the next Age.
   *
   * The one dial that separates the four personalities on the climb. Zero means
   * "the instant the law allows"; six means it would rather have the companies
   * and will take the Age when it happens to be able to. It is not a timer —
   * the bot still has to be able to *pay*, which for the upper Ages means it has
   * had to build the chain first.
   */
  ageAppetite: number;
}

/**
 * Which yard makes each resource an Age bill can name, for the save-for-the-
 * climb want. Only doors a bot can open on its own: gold arrives by tax and
 * trade rather than a building, and souls by the fighting itself, so neither
 * is listed — a bill in those is paid by playing, not by building.
 */
const RESOURCE_SOURCES: Partial<Record<string, string>> = {
  timber: 'woodcutter',
  planks: 'sawmill',
  stone: 'quarry',
  iron: 'iron_mine',
  grain: 'farm',
  bread: 'bakery',
  essence: 'wellspring',
  magewood: 'arcane_sawmill',
  runestone: 'runeworks',
  ingots: 'smeltery',
  quintessence: 'distillery',
  soulwood: 'grafting_house',
  voidstone: 'void_kiln',
  alloy: 'rune_forge',
  ashglass: 'ashworks',
  heartiron: 'great_forge',
  hollowglass: 'the_hollowing',
  gravesilver: 'mourning_forge',
};

/** Which role a unit type fills. */
export function roleOf(typeIdx: number): Role {
  const t = unitType(typeIdx);
  if ((t.flags & UF.Caster) !== 0) return 'caster';
  if ((t.flags & (UF.Large | UF.Siege | UF.Champion)) !== 0) return 'heavy';
  if ((t.flags & UF.Cavalry) !== 0) return 'horse';
  if ((t.flags & UF.Ranged) !== 0) return 'shot';
  return 'line';
}

const PLANS: Record<BotKind, BotPlan> = {
  turtle: {
    // Production first, in every plan. A thin opening — one woodcutter, one
    // farm — is what kept every bot's economy at twelve buildings for a whole
    // run, because there was never a surplus to grow with.
    buildOrder: [
      'woodcutter', 'farm', 'house', 'woodcutter', 'quarry', 'farm', 'house',
      'granary', 'barracks', 'sawmill', 'quarry', 'house', 'archery_range',
      'iron_mine', 'bakery', 'house', 'storehouse', 'smelter', 'chapel',
      'longhouse', 'armoury',
    ],
    wallRadius: 11,
    wallWaves: 1,
    armyTarget: (w) => 2 + Math.floor(w / 5),
    roleMix: { line: 0.5, shot: 0.35, horse: 0, heavy: 0.1, caster: 0.05 },
    econRadius: 16,
    castiness: 0.5,
    towerTarget: (w) => 2 + Math.floor(w / 3),
    wallAppetite: 0.8,
    ageAppetite: 3,
  },
  rush: {
    buildOrder: [
      'woodcutter', 'farm', 'barracks', 'house', 'farm', 'woodcutter',
      'archery_range', 'house', 'granary', 'quarry', 'stable', 'house',
      'sawmill', 'iron_mine', 'bakery', 'longhouse', 'drill_yard', 'armoury',
    ],
    wallRadius: 0,
    wallWaves: 99,
    armyTarget: (w) => 3 + Math.floor(w / 2.2),
    roleMix: { line: 0.4, shot: 0.2, horse: 0.3, heavy: 0.1, caster: 0 },
    econRadius: 13,
    castiness: 1,
    towerTarget: () => 0,
    wallAppetite: 0,
    ageAppetite: 6,
  },
  balanced: {
    buildOrder: [
      'woodcutter', 'farm', 'house', 'woodcutter', 'farm', 'quarry', 'barracks',
      'house', 'granary', 'sawmill', 'archery_range', 'quarry', 'house',
      'iron_mine', 'bakery', 'house', 'smelter', 'storehouse', 'longhouse',
      'chapel', 'market', 'armoury', 'stable', 'war_room', 'infirmary', 'tavern',
    ],
    wallRadius: 13,
    wallWaves: 3,
    armyTarget: (w) => 3 + Math.floor(w / 3),
    roleMix: { line: 0.42, shot: 0.26, horse: 0.16, heavy: 0.11, caster: 0.05 },
    econRadius: 19,
    castiness: 0.9,
    towerTarget: (w) => 1 + Math.floor(w / 4),
    wallAppetite: 0.55,
    ageAppetite: 1,
  },
  greedy: {
    buildOrder: [
      'woodcutter', 'farm', 'woodcutter', 'quarry', 'house', 'farm', 'sawmill',
      'quarry', 'iron_mine', 'house', 'bakery', 'granary', 'smelter', 'market',
      'storehouse', 'house', 'deep_mine', 'barracks', 'mill', 'longhouse',
      'workshop',
    ],
    wallRadius: 18,
    wallWaves: 9,
    armyTarget: (w) => Math.max(1, Math.floor(w / 4)),
    roleMix: { line: 0.5, shot: 0.3, horse: 0.1, heavy: 0.1, caster: 0 },
    econRadius: 34,
    castiness: 0.7,
    towerTarget: (w) => Math.floor(w / 6),
    wallAppetite: 0.3,
    ageAppetite: 0,
  },
};

export class Bot {
  private rng: Rng;
  private orderIdx = 0;
  private wallQuarter = 0;
  private towersBuilt = 0;
  private lastActionTick = -100;
  private placeFails = 0;
  private actions = 0;

  constructor(
    readonly kind: BotKind,
    seed: number,
  ) {
    this.rng = makeRng(deriveSeed(seed, 5150));
  }

  private get plan(): BotPlan {
    return PLANS[this.kind];
  }

  /** Emit this tick's commands. Called every tick by the harness. */
  think(w: World): Command[] {
    const out: Command[] = [];
    if (w.gameOver) return out;

    /**
     * Answer the Chronicle first, and before the action cadence gate.
     *
     * A pending event freezes the simulation, so a bot that let one sit would
     * not be playing badly — it would have ended the run, and the harness would
     * grind out its full tick budget on a world that never moved again. It
     * picks uniformly from whatever it can pay for, which is also what makes a
     * sweep sample the branches rather than one favourite line.
     */
    const pending = w.pendingEvent;
    if (pending) {
      const def = chronicleEvent(pending.id);
      const affordable: number[] = [];
      for (let i = 0; i < pending.choices.length; i++) {
        const ch = def?.choices.find((c) => c.id === pending.choices[i]);
        if (!ch?.cost || canAfford(w.res, ch.cost)) affordable.push(i);
      }
      const idx = affordable.length > 0 ? affordable[nextInt(this.rng, 0, affordable.length - 1)] : 0;
      out.push(cmd(Cmd.ResolveEvent, idx, 0, 0, 0, pending.id));
      return out;
    }

    // Take the spoils immediately — a bot that leaves relics on the table is
    // not measuring the game the player will actually play.
    if (w.relicOffer.length > 0) {
      const pool = relicsAvailable(w.relics, w.wave);
      const pick = w.relicOffer.find((id) => pool.some((r) => r.id === id)) ?? w.relicOffer[0];
      out.push(cmd(Cmd.PickRelic, 0, 0, 0, 0, pick));
      w.relicOffer = [];
    }

    // Act on a slow cadence: a human does not issue thirty orders a second.
    if (w.tick - this.lastActionTick < 12) {
      this.fight(w, out);
      return out;
    }
    this.lastActionTick = w.tick;

    if (w.phase === Phase.Build) {
      // The Age gets first call on everything, and before the army, because it
      // is a threshold purchase rather than a rate: a bot that spends its
      // magewood on Runeguards the moment it has any will never in a whole run
      // be holding the hundred and twenty the third Age asks for at once. The
      // muster resumes on the very next cadence tick.
      this.climb(w, out);
      // Study before mustering, on the same argument the Age is taken first:
      // research is a threshold purchase and a bot that spends every ingot on
      // Runeguards is never holding a node's price at once.
      this.study(w, out);
      // The army gets next call on the treasury; building spends what is left.
      //
      // This replaced an explicit "reserve" that held resources back for a
      // muster, which deadlocked spectacularly: no army meant a reserve, the
      // reserve blocked the housing and the woodcutters, and without those
      // there was never an economy to raise an army with. Ordering the two
      // decisions is all the priority that was ever needed.
      this.recruit(w, out);
      if (out.length === 0) this.build(w, out);
      this.upgradePowers(w, out);
      this.kit(w, out);
      // Once it has what it wants, it stops waiting around.
      if (this.readyToFight(w) && w.phaseT > 12) out.push(cmd(Cmd.StartWave));
    }

    this.fight(w, out);
    return out;
  }

  /**
   * Wear the best of what has been found, melt the rest, and feed what is worn.
   *
   * The harness exists to measure the game a player actually plays, and a bot
   * that leaves every find sitting on the cart measures a game with the whole
   * RPG layer switched off — the same reason it takes relics the instant they
   * are offered. "Best" is deliberately crude: rarity first, then level. A
   * player would weigh a Seal against a Banner; a bot only has to stop the
   * numbers from being wrong by a factor.
   */
  private kit(w: World, out: Command[]): void {
    if (w.hero.classId === '') return;

    // One decision per cadence tick, so the log stays readable and the bot does
    // not reshuffle its whole kit in a single frame.
    const loose = w.inventory.filter((s) => !w.equipped.includes(s.uid));
    for (const st of loose) {
      const def = itemDef(st.defId);
      if (!def) continue;
      const worn = equippedStack(w, def.slot);
      const wornDef = worn ? itemDef(worn.defId) : undefined;
      const score = (d: typeof def, lvl: number) => d.rarity * 100 + lvl;
      if (!worn || !wornDef || score(def, st.level) > score(wornDef, worn.level)) {
        out.push(cmd(Cmd.EquipItem, st.uid));
        return;
      }
    }

    // Feed the worn kit once there is a fire and coin to spare. The reserve is
    // what stops a bot from reforging its way out of an army.
    if (hasForge(w) && w.res.gold > 600 && w.res.souls > 200) {
      for (const uid of w.equipped) {
        if (uid < 0) continue;
        if (reforgeError(w, uid) === '') {
          out.push(cmd(Cmd.ReforgeItem, uid));
          return;
        }
      }
    }

    // A full cart is a cart that eats the next find. Melt the worst of it.
    if (w.inventory.length >= INVENTORY_MAX - 1 && loose.length > 0) {
      let worst = loose[0];
      for (const st of loose) {
        const a = itemDef(st.defId);
        const b = itemDef(worst.defId);
        if (!a || !b) continue;
        if (a.rarity * 100 + st.level < b.rarity * 100 + worst.level) worst = st;
      }
      out.push(cmd(Cmd.ScrapItem, worst.uid));
    }
  }

  /**
   * A competent player does not sit through the whole build timer once there is
   * nothing left to spend on — they sound the horn and bank the extra waves.
   * The bot does the same, which is both realistic and the difference between
   * a harness run reaching wave 12 and reaching wave 50.
   */
  private readyToFight(w: World): boolean {
    const strength = companyStrength(w.companies.filter((c) => c.team === Team.Player && !c.dead));
    // Wanting more soldiers is not a reason to stand in an empty field: if it
    // cannot afford anything at all, waiting buys nothing.
    const broke = w.res.timber < 40 && w.res.stone < 40 && w.res.grain < 60;
    if (strength < this.plan.armyTarget(w.wave + 1) && !(broke && strength >= 1)) return false;

    // Anything left it could usefully buy right now?
    if (this.orderIdx < this.plan.buildOrder.length) {
      const idx = buildingTypeIndex(this.plan.buildOrder[this.orderIdx]);
      if (BUILDING_TYPES[idx].tier <= w.age && canAfford(w.res, buildCostFor(w, idx))) return false;
    }
    if (this.plan.wallRadius > 0 && w.res.stone > 150) {
      const want = Math.round(2 * Math.PI * this.plan.wallRadius * 0.85);
      if (wallTileCount(w) < want) return false;
    }
    return true;
  }

  // ── The climb ────────────────────────────────────────────────────────────

  /**
   * Commit to the next Age when the law and the ledger both allow it.
   *
   * This did not exist, and its absence was quietly the largest error in the
   * harness. `Cmd.AdvanceAge` was added the day an Age stopped arriving on a
   * wave clock, and no bot ever issued it — so every sweep since has been four
   * personalities playing a first-Age game for fifty waves, reporting numbers
   * about a tier of buildings none of them could place and a roster none of them
   * could raise. Any balance figure about the Kindling or anything above it that
   * predates this line is not a wrong number; it is a number about a different
   * game.
   *
   * Deliberately not clever. There is no saving-up, no forecast, no "would I
   * rather have four companies" — it takes the Age the first cadence tick it can
   * pay for one, which is both what a competent player does and the behaviour
   * that makes the sweep's Age-reached distribution mean something. What varies
   * between the four is `ageAppetite`, and what varies far more than that is
   * whether the bot has built the chain that pays the bill at all.
   */
  private climb(w: World, out: Command[]): void {
    if (w.battle || w.researchingAge > 0 || w.age >= MAX_AGE) return;
    const next = w.age + 1;
    if (w.wave < ageWaveFloor(next) + this.plan.ageAppetite) return;
    /**
     * Raise the Hearth first, if the Age needs a bigger one.
     *
     * `AGE_HEARTH_TIER` is a floor the same way the wave is, and no bot had ever
     * issued `Cmd.UpgradeBuilding` for anything — so without this the harness
     * would have stalled at the Bell for ever and reported it as an economy
     * problem. The upgrade is wanted *only* when an Age is otherwise within
     * reach, which is what keeps it a decision rather than a reflex: a bot that
     * upgraded the Hearth whenever it could afford to would be spending the Age
     * price on beds.
     */
    if (w.hearth >= 0 && w.buildings.isAlive(w.hearth) && w.buildings.tier[w.hearth] < ageHearthTier(next)) {
      if (canAfford(w.res, upgradeCostFor(w, w.hearth))) out.push(cmd(Cmd.UpgradeBuilding, w.hearth));
      return;
    }
    if (!canAfford(w.res, ageCost(next))) return;
    out.push(cmd(Cmd.AdvanceAge));
  }

  /**
   * Buy the cheapest thing the Academy will presently sell, own column first.
   *
   * The tree has never been touched by the harness. `Cmd.Research` is issued by
   * no bot and, as of this pass, by no UI either — so sixty shared nodes, forty
   * four race nodes and the Academy gate that now stands in front of all of them
   * were reachable only from the debug console. Every sweep number about army
   * strength or wall health has therefore been a number about a settlement that
   * never learned anything, which is precisely the shape of the `AdvanceAge`
   * lie one pass ago.
   *
   * Three deliberate choices, and they are the same three `climb` makes:
   *
   *   **Own column first.** A race's fourth column is where its buildings and
   *   its binding are opened, so a bot that only ever bought Coursed Stone would
   *   leave every race playing as a slightly worse Ashen — and the race content
   *   would go untested while appearing to be covered.
   *
   *   **Cheapest affordable, not best.** There is no valuation here on purpose.
   *   A bot that ranked nodes would be measuring its own opinion of the tree
   *   rather than the tree; cheapest-first walks the whole thing given enough
   *   run, which is what a sweep wants from it.
   *
   *   **`researchError` decides, not a reimplementation of it.** Prerequisites,
   *   Age, race ownership, Academy depth and price are one function and the bot
   *   asks it. A second copy of those rules here would drift from the real gate
   *   and the harness would then be testing the copy.
   */
  private study(w: World, out: Command[]): void {
    if (w.battle) return;
    if (academyTier(w) === 0) return;
    let best: TechNode | undefined;
    for (const node of techFor(w.raceId)) {
      if (researchError(w, node.id) !== '') continue;
      if (!best) {
        best = node;
        continue;
      }
      // Own column first, then price. `costWeight` is a plain sum: the point is
      // a total order that does not depend on key iteration, not a valuation.
      const mine = node.race ? 1 : 0;
      const bestMine = best.race ? 1 : 0;
      if (mine !== bestMine) {
        if (mine > bestMine) best = node;
        continue;
      }
      if (costWeight(node.cost) < costWeight(best.cost)) best = node;
    }
    if (best) out.push(cmd(Cmd.Research, 0, 0, 0, 0, best.id));
  }

  // ── Building ─────────────────────────────────────────────────────────────

  private build(w: World, out: Command[]): void {
    const plan = this.plan;

    /**
     * Do not open more building sites than the villagers can close.
     *
     * This was the runaway behind every other symptom. `buildings.slots.count`
     * includes unfinished foundations, and `recomputePopCap` only counts
     * finished ones — so the bot laid 151 foundations, none of them ever
     * completed with 27 villagers spread across them, the population cap never
     * rose, every resource went into half-built walls, and the bot kept laying
     * more because by its own reckoning it had no working buildings. A player
     * hits exactly this wall; they just notice sooner.
     */
    const sitesOpen = this.openSites(w);

    // Rebuild, but not to the exclusion of everything else. A settlement that
    // does not put back what was smashed bleeds out; a settlement that does
    // *only* that never grows, and the harness showed bots stuck in exactly
    // that treadmill — twenty-four buildings placed across twenty-three waves.
    this.actions++;
    if (w.rubbleCount > 0 && this.actions % 3 === 0) {
      const spot = this.findRubble(w);
      if (spot) {
        out.push(cmd(Cmd.RebuildRubble, spot.x, spot.y));
        return;
      }
    }

    /**
     * Housing before anything else once the town is full — being pop-capped
     * means no army, and no army means the next wave decides it.
     *
     * **But it obeys the site limit, and this line is the whole of a runaway.**
     * The shortcut used to sit above `sitesOpen >= 4`, alongside the walls, and
     * the walls are exempt for a written reason that housing does not share: a
     * wall tile needs no villager to finish it. A house does — and
     * `recomputePopCap` counts *finished* buildings only, so a house foundation
     * laid while four others stand unfinished raises the cap by nothing at all
     * and the bot is still pop-capped on the next tick. That is exactly the
     * 151-foundations runaway the comment above describes, with housing carved
     * out of the fix.
     *
     * It was survivable while a bot could barely raise a company. The moment the
     * muster was priced at what it actually costs (see `musterAt`) the town was
     * pop-capped most of the run, this branch took every build action, and the
     * measured result was **house×47 against woodcutter×4** in a settlement that
     * died at wave 11 — a town with no economy, all of it in unfinished hovels.
     * Master's median run ends with twelve houses; that one had forty-seven.
     */
    if (sitesOpen < 4 && w.pop >= w.popCap - 2 && canAfford(w.res, buildCostFor(w, buildingTypeIndex('house')))) {
      const idx = buildingTypeIndex('house');
      const spot = this.findSpot(w, idx);
      if (spot) {
        out.push(cmd(Cmd.Build, idx, spot.x, spot.y));
        return;
      }
    }

    /**
     * The buildings this people's muster lives in jump the scripted order,
     * the way housing does.
     *
     * The Litany raises nothing at all until the Academy's column has opened
     * the Ossuary and the Ossuary is feeding the pool; the Choir's heads are
     * grown in Heartgroves behind the same gate. Both wants existed — buried
     * in `adaptiveChoices`, which only gets a turn once the 26-entry scripted
     * opening is spent, which a five-minute run barely reaches. So a bot of
     * either people played most of every run with no way to muster, and
     * `tests/bindings.test.ts` caught the Litany riding that wire the day the
     * foundations stall was fixed and the ghost labour it had been leaning on
     * went away. For everyone else the Academy really is a library and keeps
     * its ordinary place in the want list.
     */
    if (this.musterCritical(w, out)) return;

    // Walls, *before* the build order rather than after it.
    //
    // The first version gated wall-laying behind exhausting a 22-entry build
    // order and holding 200 spare stone at the same moment. Those two never
    // coincided, so across sixteen harness runs the bots laid exactly zero wall
    // tiles — which makes a siege-defence game measured by them a measurement of
    // something else entirely.
    // Walls are paid for in stone, which is not a muster cost, so a wall arc and
    // an army reserve can coexist as long as the timber holds.
    //
    // Progress is measured against the *map*, not against a counter of attempts.
    // Counting attempts meant four calls made while the treasury was empty used
    // up the whole allowance and the bot never laid a stone.
    if (plan.wallRadius > 0 && w.wave >= plan.wallWaves) {
      const laid = wallTileCount(w);
      const want = Math.round(2 * Math.PI * plan.wallRadius * 0.85);
      if (laid < want && w.res.stone > 150 && w.res.timber > 80) {
        this.layWallArc(w, out, plan.wallRadius, this.wallQuarter);
        this.wallQuarter = (this.wallQuarter + 1) % 4;
        return;
      }
    }

    // Everything below here needs villagers to finish it, so it waits on the
    // site limit. Walls do not — they go up the moment they are paid for, which
    // is why the check sits here and not at the top.
    if (sitesOpen >= 4) return;

    // Next thing on the list.
    if (this.orderIdx < plan.buildOrder.length) {
      const id = plan.buildOrder[this.orderIdx];
      const idx = buildingTypeIndex(id);
      if (BUILDING_TYPES[idx].tier <= w.age && canAfford(w.res, buildCostFor(w, idx))) {
        const spot = this.findSpot(w, idx);
        if (spot) {
          out.push(cmd(Cmd.Build, idx, spot.x, spot.y));
          this.orderIdx++;
          this.placeFails = 0;
          return;
        }
        // Nowhere legal to put it. Try a few more times before giving up on it,
        // since the ground opens up as rubble is cleared and forest is cut.
        if (++this.placeFails > 6) {
          this.orderIdx++;
          this.placeFails = 0;
        }
        return;
      }
    }

    // Towers. Best the Age allows, which is now four rungs rather than two.
    const towerIdx = buildingTypeIndex(
      w.age >= 5 ? 'knell_tower' : w.age >= 4 ? 'gilded_bastion' : w.age >= 3 ? 'voidspire' : w.age >= 2 ? 'ballista_tower' : 'arrow_tower',
    );
    if (
      this.towersBuilt < plan.towerTarget(w.wave) &&
      BUILDING_TYPES[towerIdx].tier <= w.age &&
      canAfford(w.res, buildCostFor(w, towerIdx))
    ) {
      const spot = this.findSpot(w, towerIdx, plan.wallRadius > 0 ? plan.wallRadius - 1 : 8);
      if (spot) {
        out.push(cmd(Cmd.Build, towerIdx, spot.x, spot.y));
        this.towersBuilt++;
        return;
      }
    }

    // Once the scripted opening is spent, keep growing on need.
    //
    // The first version simply stopped at the end of its build order, so bots
    // played the whole run on twelve buildings and twenty-seven villagers. A
    // competent player does not stop building; they add a fourth woodcutter
    // because timber is short, or three more hovels because they are pop-capped.
    // A *list* of wants, most urgent first — the bot takes the best one it can
    // actually pay for. Returning a single choice meant it asked for the same
    // unaffordable house every tick and never fell through to the woodcutter
    // that would have paid for it.
    /**
     * Which wants are climb provisioning, so the surplus rule can stand aside
     * for them. `hasSurplusFor`'s 2× is the right governor for *speculative*
     * building — but a mine that costs 240-timber-held is unbuildable in a
     * town that keeps timber under 200 by spending it, and pass 6 measured
     * exactly that: the provisioning wants existed and the surplus gate ate
     * them. Provisioning is the priority, not speculation; it pays list price.
     */
    const provisioning = this.climbSources(w);
    for (const want of this.adaptiveChoices(w)) {
      /**
       * Standing refusals first, and the reason is cost rather than correctness.
       *
       * `findSpot` already asks `canPlaceBuilding`, which asks `buildRefusal`, so
       * a building this people may never raise would have been refused anyway —
       * after a spiral search of several thousand tiles, every tick, for ever.
       * The Verdant Choir wants a Hovel the moment it is near its cap and may
       * never have one, so without this line a Choir bot burns its whole frame
       * budget failing to place a house it was never going to get.
       */
      if (buildRefusal(w, want) !== '') continue;
      if (provisioning.has(want)) {
        if (!canAfford(w.res, buildCostFor(w, want))) continue;
      } else if (!this.hasSurplusFor(w, want)) {
        continue;
      }
      const spot = this.findSpot(w, want);
      if (spot) {
        out.push(cmd(Cmd.Build, want, spot.x, spot.y));
        return;
      }
    }
  }

  /**
   * The yards that make what the next Age's bill is still short of — the same
   * set the provisioning block in `adaptiveChoices` wants, computed once per
   * build decision so the two cannot disagree about what counts.
   */
  private climbSources(w: World): Set<number> {
    const out = new Set<number>();
    if (w.age >= MAX_AGE) return out;
    const bill = ageCost(w.age + 1);
    for (const k of RESOURCE_KEYS) {
      const needAmt = bill[k] ?? 0;
      if (needAmt <= 0 || w.res[k] >= needAmt) continue;
      const src = RESOURCE_SOURCES[k];
      if (src) out.add(buildingTypeIndex(src));
    }
    return out;
  }

  /**
   * Place whatever this people's muster cannot exist without, ahead of the
   * scripted order. Returns true when it placed something. Only the pool and
   * grove races have anything here; everyone else pays one binding lookup.
   */
  private musterCritical(w: World, out: Command[]): boolean {
    const binding = race(w.raceId).binding;
    if (binding !== 'mana' && binding !== 'groves') return false;
    const counts = this.countBuildings(w);
    const wants: number[] = [];
    if ((counts.get('academy') ?? 0) < 1) wants.push(buildingTypeIndex('academy'));
    for (const id of race(w.raceId).buildings) {
      if (!w.unlockedBuildings.includes(id)) continue;
      const def = buildingTypeById(id);
      // The same counts `adaptiveChoices` uses for the same buildings: three
      // of a pool, six of a grove. A building that is neither is not what a
      // muster is waiting on and stays in the ordinary want list.
      const wanted = def.mana > 0 ? 3 : def.popCap > 0 ? 6 : 0;
      if (wanted > 0 && (counts.get(id) ?? 0) < wanted) wants.push(buildingTypeIndex(id));
    }
    for (const idx of wants) {
      if (buildRefusal(w, idx) !== '') continue;
      if (!canAfford(w.res, buildCostFor(w, idx))) continue;
      const spot = this.findSpot(w, idx);
      if (spot) {
        out.push(cmd(Cmd.Build, idx, spot.x, spot.y));
        return true;
      }
    }
    return false;
  }

  /** Foundations laid but not yet finished. */
  private openSites(w: World): number {
    let n = 0;
    const b = w.buildings;
    for (let i = 0; i < b.slots.high; i++) {
      if (b.slots.alive[i] === 0) continue;
      if (b.team[i] === Team.Player && b.build[i] < 1) n++;
    }
    return n;
  }

  /** Total worker slots across finished buildings. */
  private jobSlots(w: World): number {
    let n = 0;
    const b = w.buildings;
    for (let i = 0; i < b.slots.high; i++) {
      if (b.slots.alive[i] === 0 || b.build[i] < 1 || b.team[i] !== Team.Player) continue;
      n += BUILDING_TYPES[b.typeId[i]].workers;
    }
    return n;
  }

  /**
   * Governor two: only spend *surplus* on speculative building. Requires twice
   * the cost in hand, so the treasury can still accumulate toward a muster.
   */
  private hasSurplusFor(w: World, typeIdx: number): boolean {
    const cost = buildCostFor(w, typeIdx);
    for (const k of RESOURCE_KEYS) {
      const c = cost[k] ?? 0;
      if (c > 0 && w.res[k] < c * 2) return false;
    }
    return true;
  }

  /**
   * What it would reach for next, most urgent first, as building indices.
   *
   * The public face of `adaptiveChoices`, and it exists for the harness. The
   * alternative was a test that stands a bot up in the Bell and plays eighty
   * cadence ticks hoping to see a Void Kiln, which sounds more honest and is
   * not: it spends most of its length on a twenty-one entry scripted opening,
   * and it comes back red for a foundation that never finishes or a housing
   * treadmill that has nothing whatever to do with what the bot knows about the
   * third Age. Ask the question you mean.
   */
  choices(w: World): number[] {
    return this.adaptiveChoices(w);
  }

  /**
   * What the settlement is most short of right now, as a building index, or -1
   * for "do not build anything".
   *
   * Two governors, both learned from watching this go wrong. Without the first,
   * the bot built 291 buildings for 67 villagers and every one of them stood
   * empty — a settlement with no hands produces nothing, so stone sat at 9 for
   * fifteen waves. Without the second it spent every coin the instant it
   * arrived and could never accumulate the lump a company costs.
   */
  private adaptiveChoices(w: World): number[] {
    const villagerCap = Math.floor(w.popCap * VILLAGER_SHARE);
    const counts = this.countBuildings(w);
    const out: number[] = [];
    const add = (id: string) => out.push(buildingTypeIndex(id));

    // Governor one: never out-build the workforce.
    const jobSlots = this.jobSlots(w);
    const hands = Math.max(1, w.counts.villagers);
    const overbuilt = jobSlots > hands * 1.25;

    // Pop-capped means no army and no workers. Always the first problem.
    const capped = w.counts.villagers >= villagerCap - 1 || w.pop >= w.popCap - 2;
    if (capped) {
      if (w.age >= 2 && (counts.get('longhouse') ?? 0) < 4) add('longhouse');
      add('house');
    }

    /**
     * A whole arm of the army that nothing in the town can drill.
     *
     * Second only to being pop-capped, and above every stock threshold, because
     * a missing yard is not a shortage — it is a hole the settlement cannot
     * grow out of. Every plan declares a `roleMix` with horse and heavy in it,
     * and until this existed the *only* way a stable was ever raised was
     * position twenty-three of the balanced bot's scripted opening: a run that
     * died at wave forty had never once fielded cavalry, and pass 5's "five unit
     * types of sixty-two" was partly that and not a preference at all. The want
     * is self-limiting — it asks for a yard that drills a category nothing
     * drills, so the moment one stands it stops asking — and it is above the
     * workforce governor for the same reason storage is: a barracks is army
     * infrastructure, and one of them is not a treadmill.
     */
    for (const idx of this.trainerWants(w)) out.push(idx);

    // Then whatever the stores are short of — but only if there are hands to
    // work it. Another woodcutter staffed by nobody produces nothing, and the
    // harness caught this bot standing up 151 buildings for 27 villagers.
    //
    // The raw wants are count-capped, and the caps grow with the Age. Pass 5
    // caught the uncapped version building woodcutter×25 while producing 746
    // iron a whole run: `timber < 200` is true forever in a town that spends
    // as it earns, so the first want on the list soaked the entire building
    // budget and the bot never stocked an Age price. The caps make the list
    // fall through to the yards that make what the next Age actually costs.
    if (!overbuilt) {
      if (w.res.timber < 200 && (counts.get('woodcutter') ?? 0) < 6 + w.age * 2) add('woodcutter');
      if ((w.res.grain < 150 || w.fedFraction < 0.95) && (counts.get('farm') ?? 0) < 6 + w.age * 2) add('farm');
      /**
       * The other two foods, and the harness has to build them or the diet
       * is content no measured run ever sees — the same class of lie the
       * missing `AdvanceAge` told, and the reason it is written here rather
       * than left to a later pass.
       *
       * Wanted off the *appetite* rather than off a stock threshold, because
       * a weir and a lodge are what a town builds when the fields alone have
       * stopped keeping up, which is precisely what the population rungs make
       * happen. Capped low: two of each is a diet, six of each is a farm with
       * extra steps. A seed with no reachable bank simply never places the
       * fishery, and `findSpot` returning null is already how that is handled
       * for every seam in the game.
       */
      if (w.pop >= POP_PER_RUNG || w.fedFraction < 0.99 || larderWorth(w) < w.pop * 3) {
        if ((counts.get('fishery') ?? 0) < 2 + w.age) add('fishery');
        if ((counts.get('hunters_lodge') ?? 0) < 1 + w.age) add('hunters_lodge');
      }
      if (w.res.stone < 150 && (counts.get('quarry') ?? 0) < 3 + w.age) add('quarry');
      if (w.res.planks < 80 && (counts.get('sawmill') ?? 0) < 2 + w.age) add('sawmill');
      if (w.res.bread < 50 && (counts.get('bakery') ?? 0) < 2) add('bakery');
      if (w.res.iron < 100 && (counts.get('iron_mine') ?? 0) < 2 + w.age) add('iron_mine');
      if ((counts.get('granary') ?? 0) < 2) add('granary');
      if ((counts.get('storehouse') ?? 0) < 2) add('storehouse');

      /**
       * Save for the climb. `climb()` takes the next Age the moment it can pay
       * — but nothing below ever *stocked* the price, so it almost never
       * could: pass 5 measured the Age-2 economy at zero in 22 of 24 runs.
       * When a resource the next Age asks for is short, want the yard that
       * makes it, over and above the subsistence thresholds. Same caps, so a
       * funded settlement (or one already holding the price) wants nothing.
       */
      if (w.age < MAX_AGE) {
        const bill = ageCost(w.age + 1);
        for (const k of RESOURCE_KEYS) {
          const needAmt = bill[k] ?? 0;
          if (needAmt <= 0 || w.res[k] >= needAmt) continue;
          const src = RESOURCE_SOURCES[k];
          if (src && (counts.get(src) ?? 0) < 2 + w.age) add(src);
        }
      }

      /**
       * The Academy, and then whatever this people's own column opens.
       *
       * Research used to be bought off a menu and no bot ever bought any, so
       * the sixty-node tree has never been exercised by the harness at all —
       * the same class of lie the missing `AdvanceAge` told, and found the same
       * way. Now that a node needs a building, the building has to be on the
       * want list or the whole tree stays unreachable *and* the gate that makes
       * it unreachable goes untested.
       *
       * Exactly one, and early: a second Academy is not more research, it is a
       * second building, and depth comes from upgrading rather than from
       * duplicating — see `academyTier`. It sits inside the workforce governor
       * with everything else because an Academy nobody staffs still costs the
       * ground it stands on.
       */
      if ((counts.get('academy') ?? 0) < 1) add('academy');
      // Then its own identity: a race building is research-gated, so this only
      // ever fires once the column that opens it has actually been bought.
      for (const id of race(w.raceId).buildings) {
        if (!w.unlockedBuildings.includes(id)) continue;
        /**
         * How many of a race building is worth having depends on what it *is*
         * to that race, and there are now three answers rather than two. An
         * Ossuary is the Litany's pool, so three of them. A Heartgrove is the
         * Choir's *only* source of heads — it may not build a house at all — and
         * a bot that stopped at one would be capped at the Hearth for the rest
         * of the run, so it wants as many as a housed people wants Hovels. A
         * Lamp House is a building; one is plenty.
         */
        const def = buildingTypeById(id);
        const wanted = def.mana > 0 ? 3 : def.popCap > 0 ? 6 : 1;
        if ((counts.get(id) ?? 0) < wanted) add(id);
      }

      /**
       * The chain, one Age at a time.
       *
       * A bot that can climb but does not know what the Age unlocked would climb
       * once and then stand in the second Age holding an Age-1 economy, unable
       * ever to pay for the third — which is a subtler version of the same lie
       * the missing `AdvanceAge` told. Each rung wants a source before it wants
       * a converter (no Wellspring, no essence, and three of the Kindling's four
       * recipes stop dead), and each converter is asked for only when the
       * settlement is actually short of what it makes.
       *
       * All of it sits inside the workforce governor, and correctly so: a Void
       * Kiln with nobody in it is a very expensive shed.
       */
      if (w.age >= 2) {
        if ((counts.get('wellspring') ?? 0) < w.age) add('wellspring');
        if (w.res.magewood < 200 && (counts.get('arcane_sawmill') ?? 0) < 3) add('arcane_sawmill');
        if (w.res.runestone < 200 && (counts.get('runeworks') ?? 0) < 3) add('runeworks');
        if (w.res.ingots < 200 && (counts.get('smeltery') ?? 0) < 3) add('smeltery');
      }
      if (w.age >= 3) {
        // Souls stop being pocket money at the Bell: three of its four recipes
        // drink them, and the furnace is the only thing that makes them to order.
        if ((counts.get('soul_furnace') ?? 0) < 3) add('soul_furnace');
        if (w.res.quintessence < 250 && (counts.get('distillery') ?? 0) < 4) add('distillery');
        if (w.res.soulwood < 250 && (counts.get('grafting_house') ?? 0) < 3) add('grafting_house');
        if (w.res.voidstone < 250 && (counts.get('void_kiln') ?? 0) < 3) add('void_kiln');
        if (w.res.alloy < 250 && (counts.get('rune_forge') ?? 0) < 3) add('rune_forge');
      }
      if (w.age >= 4) {
        if (w.res.ashglass < 300 && (counts.get('ashworks') ?? 0) < 3) add('ashworks');
        if (w.res.heartiron < 300 && (counts.get('great_forge') ?? 0) < 3) add('great_forge');
      }
      if (w.age >= 5) {
        // Two, and no more than two, whatever it is short of. A bot with no
        // opinion about its own population would happily build ten and hollow
        // out the town that staffs the rest of the chain.
        if (w.res.hollowglass < 300 && (counts.get('the_hollowing') ?? 0) < 2) add('the_hollowing');
        if (w.res.gravesilver < 300 && (counts.get('mourning_forge') ?? 0) < 2) add('mourning_forge');
      }
    }

    // Storage and beds are not production and do not answer to the workforce
    // governor. The Age prices are three figures apiece and a price that does
    // not fit in a shed cannot be paid at all — see `vault` in `buildings.ts`.
    if (w.age >= 3 && (counts.get('vault') ?? 0) < 3) add('vault');
    if (w.age >= 4 && (counts.get('gilded_hall') ?? 0) < 2) add('gilded_hall');

    // Comfortable: spend the surplus on housing, which becomes more of
    // everything else in time.
    if (!capped) add('house');
    return out;
  }

  /**
   * A yard for every arm the plan asks for and nothing in town can drill, as
   * building indices.
   *
   * Asked of the *roster* rather than of a table of yard names, so it works for
   * a people nobody wrote a build order for: for each role the plan wants, find
   * a unit of that role this Age allows, ask `hasTrainer` — the same function
   * `Cmd.Recruit` refuses on — and if nothing drills it, take the cheapest
   * building this settlement is allowed to raise that does. `buildRefusal` is
   * asked here rather than left to the placement spiral for the reason the
   * build loop gives: a people that may never have this yard would otherwise
   * pay for a several-thousand-tile search of ground it was never going to get.
   */
  private trainerWants(w: World): number[] {
    const out: number[] = [];
    for (const r of ROLES) {
      if ((this.plan.roleMix[r] ?? 0) <= 0) continue;
      let need = '';
      for (const id of w.faction.roster) {
        let idx: number;
        try {
          idx = unitTypeIndex(id);
        } catch {
          continue;
        }
        if (unitType(idx).tier > w.age || roleOf(idx) !== r) continue;
        // One drillable unit in the role is the whole arm answered for.
        if (hasTrainer(w, idx)) {
          need = '';
          break;
        }
        if (need === '') need = trainerCategoryFor(idx);
      }
      if (need === '') continue;
      let pick = -1;
      let pickCost = Infinity;
      for (let i = 0; i < BUILDING_TYPES.length; i++) {
        const def = BUILDING_TYPES[i];
        if (def.tier > w.age || !def.trains.includes(need)) continue;
        if (buildRefusal(w, i) !== '') continue;
        const c = costWeight(buildCostFor(w, i));
        if (c < pickCost) {
          pick = i;
          pickCost = c;
        }
      }
      if (pick >= 0) out.push(pick);
    }
    return out;
  }

  private countBuildings(w: World): Map<string, number> {
    const m = new Map<string, number>();
    const b = w.buildings;
    for (let i = 0; i < b.slots.high; i++) {
      if (b.slots.alive[i] === 0) continue;
      const id = BUILDING_TYPES[b.typeId[i]].id;
      m.set(id, (m.get(id) ?? 0) + 1);
    }
    return m;
  }

  /**
   * Spiral out from the Hearth looking for legal ground.
   *
   * Terrain-gated buildings (woodcutter, quarry, mine) get a much longer
   * leash, **scaled to the map**: the old constant of 58 was tuned when the
   * map was 128 tiles and the seams sat twenty to sixty out. The map is 384
   * now, and pass 6's stubborn seed had *no legal iron-mine site inside 58
   * tiles at any point in the run* — affordable from minute ten, placeable
   * never, iron production zero for the whole game and the roster capped at
   * militia because of it. Beyond the old radius the spiral coarsens to
   * every other ring: at that distance the question is "does a seam exist
   * out here at all", and half the tile tests answer it just as well.
   */
  private findSpot(w: World, typeIdx: number, maxR?: number): { x: number; y: number } | null {
    const def = BUILDING_TYPES[typeIdx];
    const terrainLeash = Math.floor(Math.max(MAP_W, MAP_H) * 0.35);
    const limit =
      maxR ?? (def.needsTerrain || def.needsNearTerrain ? terrainLeash : this.plan.econRadius);
    const cx = w.map.hearthX;
    const cy = w.map.hearthY;
    const start = nextInt(this.rng, 0, 360);
    for (let r = 4; r <= limit; r += r > 60 ? 2 : 1) {
      const steps = Math.max(10, r * 5);
      for (let s = 0; s < steps; s++) {
        const a = ((start + (s * 360) / steps) % 360) * 0.0174533;
        const x = Math.round(cx + Math.cos(a) * r);
        const y = Math.round(cy + Math.sin(a) * r);
        if (canPlaceBuilding(w, typeIdx, x, y) === '') return { x, y };
      }
    }
    return null;
  }

  /** Any rubble tile the bot can afford to put back. */
  private findRubble(w: World): { x: number; y: number } | null {
    for (let k = 0; k < w.map.rubble.length; k++) {
      const mark = w.map.rubble[k];
      if (mark === 0) continue;
      const typeIdx = mark - 1;
      if (!canAfford(w.res, buildCostFor(w, typeIdx))) continue;
      return { x: k % w.map.w, y: (k / w.map.w) | 0 };
    }
    return null;
  }

  /** One quarter of a wall ring per call, so the cost is spread over waves. */
  private layWallArc(w: World, out: Command[], radius: number, quarter: number): void {
    const cx = w.map.hearthX;
    const cy = w.map.hearthY;
    const a0 = (quarter * Math.PI) / 2;
    const a1 = a0 + Math.PI / 2;
    const steps = 6;
    let prev: { x: number; y: number } | null = null;
    for (let i = 0; i <= steps; i++) {
      const a = a0 + ((a1 - a0) * i) / steps;
      const p = { x: Math.round(cx + Math.cos(a) * radius), y: Math.round(cy + Math.sin(a) * radius) };
      if (prev) {
        // Leave one gate per quarter so the settlement is not sealed shut.
        const gate = i === Math.floor(steps / 2);
        out.push(cmd(Cmd.WallLine, prev.x, prev.y, p.x, p.y, gate ? 'gate' : ''));
      }
      prev = p;
    }
  }

  // ── Army ─────────────────────────────────────────────────────────────────

  /**
   * Everything this faction can currently drill, grouped by role. Recomputed
   * rather than cached because what it can drill changes as buildings finish and
   * as Ages turn.
   */
  private availableByRole(w: World): Map<Role, number[]> {
    const out = new Map<Role, number[]>();
    for (const r of ROLES) out.set(r, []);
    for (const id of w.faction.roster) {
      let idx: number;
      try {
        idx = unitTypeIndex(id);
      } catch {
        continue;
      }
      const t = unitType(idx);
      if (t.tier > w.age) continue;
      if (!hasTrainer(w, idx)) continue;
      (out.get(roleOf(idx)) as number[]).push(idx);
    }
    // Best (highest tier) first within each role — a bot with the resources for
    // Men-at-Arms should not keep buying militia.
    for (const list of out.values()) list.sort((a, b) => unitType(b).tier - unitType(a).tier);
    return out;
  }

  private recruit(w: World, out: Command[]): void {
    const alive = w.companies.filter((c) => c.team === Team.Player && !c.dead);
    // Reinforce before recruiting: replacements are cheaper than a new company.
    //
    // Affordability is checked here rather than left to the command, because an
    // order that silently fails still consumes the bot's action for that tick —
    // and a company below strength that it cannot pay for meant it re-issued a
    // doomed Reinforce forever and never built anything again.
    for (const c of alive) {
      const missing = c.maxSize - c.members.length;
      if (missing <= 0 || c.members.length >= c.maxSize * 0.65) continue;
      const t = unitType(c.typeIdx);
      /**
       * The same split `reinforceCompany` charges, and it stopped being the same
       * the day a company could be raised short.
       *
       * The 0.85 replacement rate is capped at the casualties this company has
       * actually taken — anybody filling a roll that was never full pays list
       * price for the difference. This precheck priced *every* missing man at
       * 0.85, so a bot looking at a company raised eleven of forty believed the
       * replacements were fifteen per cent cheaper than the command would charge
       * it, issued the order, and had it refused on `canAfford` — spending the
       * tick's one action and coming back to the same company next tick. That is
       * precisely the doomed-order loop the comment above this block was written
       * about, reopened by the establishments growing. One price, in two places,
       * derived the same way.
       */
      const replacing = Math.min(missing, c.losses);
      const men = replacing * 0.85 + (missing - replacing);
      const cost: Record<string, number> = {};
      for (const k of RESOURCE_KEYS) {
        const v = (t.cost[k] ?? 0) * men * w.mods.reinforceCost;
        if (v > 0) cost[k] = v;
      }
      if (!canAfford(w.res, cost)) continue;
      /**
       * And whether this people may replace anybody at all.
       *
       * `reinforceError` rather than a reimplementation of it, for the reason
       * the research loop below gives about `researchError`: the rule and the
       * harness must not be two different pieces of code. Without this a Silver
       * Reaches bot spent every tick of every run re-issuing a `Reinforce` that
       * `reinforceCompany` refuses on the first line, never fell through to the
       * recruit below it, and never built anything — so every balance number
       * ever taken for that race was taken from a settlement standing still.
       * That is the same class of lie the missing `AdvanceAge` told.
       */
      if (reinforceError(w, c) !== '') continue;
      out.push(cmd(Cmd.Reinforce, c.id));
      return;
    }
    if (companyStrength(alive) >= this.plan.armyTarget(w.wave + 1)) return;

    // What the army is currently made of, by role.
    const have: Record<Role, number> = { line: 0, shot: 0, horse: 0, heavy: 0, caster: 0 };
    for (const c of alive) have[roleOf(c.typeIdx)] += c.members.length;
    const total = Math.max(1, alive.reduce((n, c) => n + c.members.length, 0));

    // Biggest shortfall against the target mix wins.
    const byRole = this.availableByRole(w);
    const wanted = [...ROLES]
      .map((r) => ({ role: r, deficit: (this.plan.roleMix[r] ?? 0) - have[r] / total }))
      .filter((x) => (this.plan.roleMix[x.role] ?? 0) > 0)
      .sort((a, b) => b.deficit - a.deficit);

    const held = new Map<number, number>();
    for (const c of alive) held.set(c.typeIdx, (held.get(c.typeIdx) ?? 0) + 1);

    for (const { role } of wanted) {
      const idx = this.pickInRole(w, byRole.get(role) ?? [], held);
      if (idx < 0) continue;
      out.push(cmd(Cmd.Recruit, idx));
      return;
    }
  }

  /**
   * How many men a company of this type would actually march out with, right
   * now, or zero if it is not worth raising a banner over.
   *
   * **Ask the muster what it costs; do not price the establishment.** This used
   * to be `canAfford(cost × companySize)` on one side and `musterRoom` on the
   * other, which was two different rules for one question and the wrong one on
   * the side that mattered. `recruitCompany` raises a company at whatever
   * strength the beds, the grown hands, the binding and the purse allow, down to
   * `minMuster` — so a bot that saved for the whole roll was saving for a price
   * the simulation never charges. Pass 8 measured what that cost: the opening
   * muster arrived at a median of 38.0k ticks against 23.5k before the
   * establishments grew, because a town of three hundred timber stood there
   * unable to "afford" a militia company it could in fact have raised
   * seventeen strong on tick one. It also flattened the roster: the cheapest
   * thing in the role was the only thing whose *whole* establishment ever came
   * within reach, which is precisely the "Militia in 100% of builds" reading.
   *
   * `musterStrength` and `affordableModels` are the two halves `recruitCompany`
   * itself uses, called here rather than reimplemented, for the same reason the
   * reinforce loop calls `reinforceError`: the rule and the harness must not be
   * two different pieces of code. That also keeps every binding — mana, names,
   * thralls, moot, housing — answered by the one function that knows about them.
   */
  private musterAt(w: World, typeIdx: number): number {
    const t = unitType(typeIdx);
    const est = t.companySize;
    const n = Math.min(musterStrength(w, typeIdx, est).n, affordableModels(w, t.cost, est));
    return n >= minMuster(est) ? n : 0;
  }

  /**
   * The best thing this role can put on the field today.
   *
   * Three keys, in order, and the order is the whole of the design:
   *
   * 1. **A full-ish company beats a skeleton.** `minMuster` lets a bot raise a
   *    quarter-strength banner, which is the right *floor* — it is what gets an
   *    army onto the field in the opening waves — and a very poor *habit*. A bot
   *    that always took the highest tier it could scrape a quarter of would field
   *    five Men-at-Arms where forty militia were affordable, and the harness
   *    would read that as diversity while the town died. So anything that comes
   *    in at `MUSTER_SOLID_SHARE` or better outranks anything that does not, and
   *    the skeleton is only reached when nothing in the role is solid.
   * 2. **Then the best tier**, which is what the role list is already sorted by.
   *    This is the half `NEXT.md` asked for: the muster should *prefer* the best
   *    affordable tier, not merely be allowed it.
   * 3. **Then whatever the army has fewest banners of.** Only ever a tie-break
   *    *within* a tier, so it can never buy a worse unit than the one it passed
   *    over — but it is why the Ashfield Spears stand next to the Hearth Militia
   *    and the Ashmare Riders next to the Grey Outriders instead of the roster's
   *    declaration order deciding forever. Diversity that costs nothing is the
   *    only kind worth taking.
   */
  private pickInRole(w: World, candidates: number[], held: Map<number, number>): number {
    let best = -1;
    let bestKey = -Infinity;
    for (const idx of candidates) {
      const n = this.musterAt(w, idx);
      if (n === 0) continue;
      const t = unitType(idx);
      const solid = n >= Math.ceil(t.companySize * MUSTER_SOLID_SHARE) ? 1 : 0;
      const key = solid * 1e6 + t.tier * 1e3 - (held.get(idx) ?? 0);
      if (key > bestKey) {
        bestKey = key;
        best = idx;
      }
    }
    return best;
  }

  private upgradePowers(w: World, out: Command[]): void {
    if (w.res.souls < 200) return;
    for (const id of w.unlockedPowers) {
      const tier = w.powerTiers[id] ?? 0;
      if (tier >= 3) continue;
      const def = powerOrNull(id);
      if (!def) continue;
      if (w.res.souls >= def.tiers[tier].soulCost * 1.5) {
        out.push(cmd(Cmd.UpgradePower, 0, 0, 0, 0, id));
        return;
      }
    }
  }

  // ── Fighting ─────────────────────────────────────────────────────────────

  private fight(w: World, out: Command[]): void {
    if (w.phase !== Phase.Siege) return;

    // Find the biggest knot of invaders and send everything at it.
    const threat = this.biggestThreat(w);
    if (!threat) return;

    if (w.tick % 20 === 0) {
      for (const c of w.companies) {
        if (c.team !== Team.Player || c.dead) continue;
        const t = unitType(c.typeIdx);
        // Missile companies hang back a little; the line goes in.
        const standoff = (t.flags & UF.Ranged) !== 0 ? 5 : 1.2;
        const dx = w.map.hearthX - threat.x;
        const dy = w.map.hearthY - threat.y;
        const l = Math.hypot(dx, dy) || 1;
        out.push(
          cmd(
            Cmd.MoveCompany,
            c.id,
            threat.x + (dx / l) * standoff,
            threat.y + (dy / l) * standoff,
            1,
          ),
        );
        if ((t.flags & UF.Spear) !== 0) out.push(cmd(Cmd.SetFormation, c.id, Formation.Shieldwall));
        if (c.stance !== Stance.Guard) out.push(cmd(Cmd.SetStance, c.id, Stance.Guard));
      }
    }

    // Powers: spend on the densest cluster once there is enough Will.
    if (nextFloat(this.rng) < this.plan.castiness * 0.35) {
      for (const id of w.unlockedPowers) {
        const def = powerOrNull(id);
        if (!def) continue;
        if ((w.powerCooldowns[id] ?? 0) > 0) continue;
        const r = resolvePower(w, def);
        if (w.will < r.cost) continue;
        // Never drop a friendly-fire power on top of our own line.
        if (def.friendlyFire && threat.friendlyNear > 3) continue;
        out.push(cmd(Cmd.CastPower, threat.x, threat.y, 1, 0, id));
        break;
      }
    }
  }

  private biggestThreat(w: World): { x: number; y: number; n: number; friendlyNear: number } | null {
    const u = w.units;
    let bx = 0;
    let by = 0;
    let n = 0;
    for (let i = 0; i < u.slots.high; i++) {
      if (u.slots.alive[i] === 0) continue;
      // Alliance rather than banner, so a second horde is part of the same
      // threat centroid rather than invisible to the bot.
      if (!areHostile(u.team[i], Team.Player)) continue;
      bx += u.x[i];
      by += u.y[i];
      n++;
    }
    if (n === 0) return null;
    const x = bx / n;
    const y = by / n;
    let friendlyNear = 0;
    for (let i = 0; i < u.slots.high; i++) {
      if (u.slots.alive[i] === 0) continue;
      if (!alliedWith(u.team[i], Team.Player)) continue;
      if ((u.x[i] - x) ** 2 + (u.y[i] - y) ** 2 < 16) friendlyNear++;
    }
    return { x, y, n, friendlyNear };
  }
}

/** How much wall the settlement currently has, for the harness report. */
/**
 * A plain sum of a cost, for ordering nodes cheapest-first.
 *
 * Not a valuation and deliberately not weighted by rarity: souls and timber
 * count the same here, which is wrong as economics and right as a *total order*
 * — the only thing asked of it is that two nodes compare the same way on every
 * machine and in every replay. Anything cleverer would make the harness measure
 * the bot's opinion of the tree instead of the tree.
 */
function costWeight(cost: Partial<Record<string, number>>): number {
  let n = 0;
  for (const k of RESOURCE_KEYS) n += cost[k] ?? 0;
  return n;
}

/**
 * An army measured in companies' *worth* of men rather than in banners.
 *
 * `armyTarget` is authored as "how many companies it wants by wave n", and that
 * was an unambiguous quantity right up until a company stopped being raised
 * whole. Now a banner is anything from a quarter of an establishment to all of
 * it, so counting banners lets a bot with seven quarter-strength companies
 * believe it has the army of seven — and `readyToFight` then sounds the horn on
 * it. A half-full company counts a half. Nothing else about the plans changes,
 * and at full strength this is exactly the number it always was.
 */
function companyStrength(companies: { members: unknown[]; maxSize: number }[]): number {
  let n = 0;
  for (const c of companies) n += c.members.length / Math.max(1, c.maxSize);
  return n;
}

export function wallTileCount(w: World): number {
  let n = 0;
  for (let i = 0; i < w.map.wall.length; i++) if (w.map.wall[i] !== WallKind.None) n++;
  return n;
}

export function roadTileCount(w: World): number {
  let n = 0;
  for (let i = 0; i < w.map.terrain.length; i++) if (w.map.terrain[i] === Terrain.Road) n++;
  return n;
}

export function botFor(kind: BotKind, seed: number): Bot {
  return new Bot(kind, seed);
}
